import "leaflet/dist/leaflet.css";
import { useMemo } from "react";
import {
    Circle,
    MapContainer,
    // Popup,
    TileLayer,
} from "react-leaflet";
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import CsvDownloadButton from "react-json-to-csv";
import type { ComplianceFuelStop, TractorCompliance } from "./api";

interface TractorFuelStopsComplianceProps {
    data: TractorCompliance;
    onClose: () => void;
}

const TractorFuelStopsCompliance = ({ data, onClose }: TractorFuelStopsComplianceProps) => {
    const columns = useMemo<ColumnDef<ComplianceFuelStop>[]>(
        () => [
            {
                header: "Solution Date",
                id: "solutionDate",
                accessorFn: (row) => new Date(row.SolutionDate ?? "").toLocaleDateString("en-US"),
            },
            {
                header: "Segment",
                accessorKey: "Segment",
            },
            {
                header: "Tractor",
                accessorKey: "Tractor",
            },
            {
                header: "Lane",
                id: "lane",
                accessorFn: (row) => `${row.TractorOrigin} - ${row.TractorDestination}`,
            },
            {
                header: "Station",
                accessorKey: "Station",
            },
            {
                header: "Score",
                id: "score",
                accessorFn: (row) => (row.compliance === 1 ? 100 : 0),
                cell: ({ getValue }) => `${getValue<number>()}%`,
            },
            {
                header: "Optimal PPG",
                accessorKey: "OptimalPPG",
            },
        ],
        []
    );

    const tableData = useMemo(() => data.fuelStops, [data]);

    // Filter out fuel stops with null coordinates. The type predicate narrows the
    // element type so Latitude/Longitude are non-null numbers downstream.
    const validFuelStops = useMemo(
        () =>
            tableData.filter(
                (stop): stop is ComplianceFuelStop & { Latitude: number; Longitude: number } =>
                    stop.Latitude != null && stop.Longitude != null
            ),
        [tableData]
    );

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-4 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                    &#8203;
                </span>
                <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-4 sm:w-full sm:max-w-4xl">
                    <div className="bg-white p-4">
                        <div className="relative">
                            <div className="flex justify-between items-center mb-4">
                                <CsvDownloadButton
                                    className="bg-green-600 text-white px-2 py-1 rounded"
                                    data={tableData}
                                    headers={[
                                        "SolutionDate",
                                        "Cust",
                                        "Segment",
                                        "Tractor",
                                        "TractorOrigin",
                                        "TractorDestination",
                                        "Station",
                                        "compliance",
                                        "City",
                                        "State",
                                        "Latitude",
                                        "Longitude",
                                        "store_number",
                                        "OptimalPPG",
                                    ]}
                                    delimiter=","
                                >
                                    Download Data
                                </CsvDownloadButton>
                                <button onClick={onClose} className="font-medium text-xl cursor-pointer">
                                    x
                                </button>
                            </div>
                            {/* Map */}
                            <div className="overflow-hidden" style={{ height: "250px" }}>
                                <MapContainer
                                    center={[37.09, -95.71] as [number, number]}
                                    zoom={4}
                                    style={{ height: "100%", width: "100%" }}
                                >
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                    {validFuelStops.map((fuelStop, i) => (
                                        <Circle
                                            key={`${fuelStop.Station}-${i}`}
                                            center={[fuelStop.Latitude, fuelStop.Longitude] as [number, number]}
                                            fillColor={fuelStop.compliance === 1 ? "green" : "red"}
                                            color={fuelStop.compliance === 1 ? "green" : "red"}
                                            fillOpacity={0.7}
                                            radius={5000}
                                        >
                                            {/* <Popup>
                                                <div>
                                                    <p>Station: {fuelStop.Station}</p>
                                                    <p>Compliance: {fuelStop.compliance === 1 ? "Compliant" : "Non-compliant"}</p>
                                                </div>
                                            </Popup> */}
                                        </Circle>
                                    ))}
                                </MapContainer>
                            </div>
                            <div className="mt-2" style={{ height: "200px", overflowY: "auto" }}>
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map((header) => (
                                                    <th
                                                        key={header.id}
                                                        className="bg-gray-50 sticky top-0 group px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </th>
                                                ))}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {table.getRowModel().rows.map((row) => (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map((cell) => (
                                                    <td
                                                        key={cell.id}
                                                        className="px-6 py-2 whitespace-nowrap"
                                                    >
                                                        {flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TractorFuelStopsCompliance;
